import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { upper } from 'to-case';
import { MediaManagerSDKUploadingFileErrorInfo } from '../../../../utils/media-manager-sdk';
import { TErrorArgs } from './UploadingFileError.types';
import { UploadError } from '../../../../constants/UploadError';
import { HelpLink } from '../../../../components/common/HelpLink';
import { HELP_LINKS } from '../../../../constants/HelpLinks';
import styles from './UploadingFileError.scss';
import { TextButton } from '../../../../components/common';
import { usePremium } from '../UploadingFile/usePremium';
import {
  useTrackLearnMoreClickAnalytics,
  ClickOrigin,
} from '../../../../utils/hooks/bi/useTrackLearnMoreClickAnalytics';
import { useStorageQuota } from '../../../../utils/hooks/storageQuota/useStorageQuota';

const cta =
  (link: string, onClick) =>
  (...chunks) =>
    (
      <HelpLink article={link} className={styles.uploadError} size="tiny" onClick={onClick}>
        {chunks}
      </HelpLink>
    );

const upgradeLink =
  (premiumUrl: string) =>
  (...chunks) => {
    return (
      <TextButton as="a" target="_blank" href={premiumUrl} skin="premium" size="tiny" className={styles.upgrade}>
        {chunks}
      </TextButton>
    );
  };

export const useExtractErrorArgs = () => {
  const { formatNumber } = useIntl();

  const { upgradeUrl: upgradeUrlDefault } = usePremium();
  const storageQuotaUpgradeUrl = useStorageQuota().storageQuotaData?.upgradeUrl;

  const { trackLearnMoreClick } = useTrackLearnMoreClickAnalytics();

  const handleHelpLinkClick = useCallback(
    (errorType: string) => () => trackLearnMoreClick(ClickOrigin.AddMediaBar, errorType),
    [trackLearnMoreClick],
  );

  const { formatMessage } = useIntl();

  return useCallback((errorInfo: MediaManagerSDKUploadingFileErrorInfo): TErrorArgs => {
    switch (errorInfo.key) {
      case UploadError.SVGEmbeddedImage:
      case UploadError.SVGNoVisualContent:
        return {
          learnMoreLink: cta(HELP_LINKS.UPLOAD_VECTOR_ART, handleHelpLinkClick(errorInfo.key)),
          learnMoreText: formatMessage({ id: 'common.learn-more' }),
        };

      case UploadError.NoResponseError:
      case UploadError.FailedResume:
      case UploadError.UploadStopped:
        return {
          learnMoreLink: cta(HELP_LINKS.RESUME_UPLOAD, handleHelpLinkClick(errorInfo.key)),
        };
      case UploadError.SizeLimit: {
        const sizeLimit = errorInfo.args[1];
        const sizeSuffix = sizeLimit.match(/\D+$/)[0];
        const readableFileSize = formatNumber(parseFloat(sizeLimit), { maximumFractionDigits: 1 });
        const sizeLimitKeys = {
          GB: 'Storage.Media_Size_Label.GB',
          MB: 'Storage.Media_Size_Label.MB',
          KB: 'Storage.Media_Size_Label.KB',
        };

        return {
          sizeLimit: sizeLimitKeys[sizeSuffix.toUpperCase()]
            ? formatMessage({ id: sizeLimitKeys[sizeSuffix.toUpperCase()] }, { amount: readableFileSize })
            : `${readableFileSize}${sizeSuffix}`,
          learnMoreLink: cta(HELP_LINKS.UPLOAD_GENERAL, handleHelpLinkClick(errorInfo.key)),
        };
      }

      case UploadError.FontsNotAllowed:
        return {
          learnMoreLink: cta(HELP_LINKS.LEARN_MORE_FONTS_UPLOAD, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.Mp4UnsupportedStereoOnly:
        return {
          learnMoreLink: cta(HELP_LINKS.UPLOAD_VIDEO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.ImageDimentionsExceeded_limit:
      case UploadError.CorruptedImage:
        return {
          learnMoreLink: cta(HELP_LINKS.UPLOAD_IMAGE_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.UnsupportedExtension:
        return {
          extension: upper(errorInfo.args[0]),
          learnMoreLink: cta(HELP_LINKS.LEARN_MORE_SUPPORTED_TYPES_AND_SIZES, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.UnsupportedVideoCodec:
        return {
          codec: errorInfo.args[0],
          learnMoreLink: cta(HELP_LINKS.UPLOAD_VIDEO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.ExternalFileForbidden:
      case UploadError.ExternalFileAuthorization:
      case UploadError.WavMissingChunk:
      case UploadError.WavMissingHeader:
      case UploadError.MIMEMismatch:
      case UploadError.InvalidFile:
      case UploadError.IncorrectFileInfo:
      case UploadError.MissingWixSession:
      case UploadError.SessionExpired:
      case UploadError.InvalidSession:
      case UploadError.General:
        return {
          learnMoreLink: cta(HELP_LINKS.UPLOAD_GENERAL, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.UnsupportedAudioCodec:
        return {
          codec: errorInfo.args[0],
          learnMoreLink: cta(HELP_LINKS.UPLOAD_AUDIO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.Mp3UnsupportedStereoOnly:
      case UploadError.ZeroSizeFile:
      case UploadError.WavUnsupportedChunkSize:
      case UploadError.WavUnsupportedSampleSize:
      case UploadError.WavUnsupportedSampleRate:
      case UploadError.WavUnsupportedStereoOnly:
        return {
          learnMoreLink: cta(HELP_LINKS.UPLOAD_AUDIO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.Mp4UnsupportedBitrate:
      case UploadError.Mp4UnsupportedSampleRate:
      case UploadError.Mp4UnsupportedFormat:
      case UploadError.Mp3UnsupportedFormat:
      case UploadError.AacUnsupportedFormat:
      case UploadError.WmaUnsupportedBitrateLossy:
      case UploadError.WmaUnsupportedBitrate:
      case UploadError.WavUnsupportedFormat:
      case UploadError.UnsupportedFormat:
        return {
          learnMoreLink: cta(HELP_LINKS.LEARN_MORE_SUPPORTED_TYPES_AND_SIZES, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.MaxVideoBitrateExceeded:
        return {
          fps: errorInfo.args[0],
          learnMoreLink: cta(HELP_LINKS.UPLOAD_VIDEO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.VideoDurationMismatch:
        return {
          videoDuration: errorInfo.args[0],
          audioDuration: errorInfo.args[1],
          learnMoreLink: cta(HELP_LINKS.UPLOAD_VIDEO_TROUBLESHOOTING, handleHelpLinkClick(errorInfo.key)),
        };

      case UploadError.QuotaFreeVideoDurationExceeded:
        return {
          usage: errorInfo.args[0],
          limit: errorInfo.args[1],
          cta: upgradeLink(upgradeUrlDefault),
        };

      case UploadError.QuotaTotalStorageExceeded:
        return {
          usage: errorInfo.args[0],
          limit: errorInfo.args[1],
          cta: upgradeLink(storageQuotaUpgradeUrl || upgradeUrlDefault),
        };

      case UploadError.QuotaTotalVideoDurationExceeded:
      case UploadError.QuotaSingleVideoDurationExceeded:
        return {
          usage: errorInfo.args[0],
          limit: errorInfo.args[1],
          cta: upgradeLink(upgradeUrlDefault),
        };

      default:
        return {};
    }
  }, []);
};
